// src/components/ActiveSwarmCard/ActiveSwarmCard.js

import React, { useState, useRef, useEffect, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import './ActiveSwarmCard.css';
import { useSelector, useDispatch } from "react-redux";
import { useTheme } from '@mui/material/styles';
import { copyToClipBoard } from "../../../../../../utils";
import { keyframes } from '@mui/system';
import { useNotification } from '../../../../../../context/NotificationContext';
import { createErrorMessage } from '../../../../../../utils';
import SwarmEditModal from '../EditEvent/SwarmEditModal';

import SwarmCardHeader from './SwarmCardHeader';
import EventInfo from './EventInfo';
import AdditionalInfo from './AdditionalInfo';
import GradientOverlay from './GradientOverlay';
import { selectSwarmSelected, setSwarmIdSelected } from "../../../../../../store/slices/swarmsInAction";
import { useEditSwarmMutation } from '../../../../../../services/swarmsApi';
import SignalREncorehiveContext from '@/context/signalREncorehive';
import { selectHasPendingBuyRequestsForSwarm } from '@/store/slices/swarms';
import { selectCurrentUserId } from '@/features/auth/authSlice';
import { getBuyManagerByEncoreHiveId } from '@/store/slices/buyManager/buyManagers';

// Shadow styles for different card states
const CARD_SHADOWS = {
  // Red shadow indicating pending items
  PENDING: '0 0 15px rgba(244, 67, 54, 0.8)',
  // Default subtle shadow
  DEFAULT: '0 2px 5px rgba(0,0,0,0.1)'
};

/**
 * ActiveSwarmCard Component
 * Displays a card with swarm event information, including gradient and highlight effects.
 * Visual states:
 * - Default: Subtle shadow
 * - Pending: Red glow effect
 * - Selected: Thick orange border
 * 
 * @param {Object} props
 * @param {SwarmAdminToolRes} props.swarm - Swarm object with event data
 * @returns {JSX.Element}
 */
const ActiveSwarmCard = ({ swarm }) => {
  const [localSwarm, setLocalSwarm] = useState(swarm);
  const userId = useSelector(selectCurrentUserId);
  const buyManager = useSelector(state => getBuyManagerByEncoreHiveId(state, userId));

  const { showNotification } = useNotification();
  
  const [highlight, setHighlight] = useState(false);
  const [gradientActive, setGradientActive] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false); // Agregado el estado para el modal de edición
  // Reference to store the highlight timeout so it can be cleared if necessary
  const highlightTimeoutRef = useRef(null);
  const theme = useTheme();
  const dispatch = useDispatch();

  // Access the SignalR connection from context
  const { signalREncorehive } = useContext(SignalREncorehiveContext);
  const [editSwarm, { isLoading: editSwarmIsLoading, error: editSwarmError, data: editSwarmData }] = useEditSwarmMutation();
  const [error, setError] = useState(null);

  useEffect(() => {
    if (editSwarmError) {
      showNotification(createErrorMessage(editSwarmError), 'error');
    }
  }, [editSwarmError]);

  const swarmSelected = useSelector(selectSwarmSelected);
  const isSelected = swarmSelected?.id === swarm.id;
  const hasPending = useSelector(state => selectHasPendingBuyRequestsForSwarm(state, swarm.id));

  // Monitor hasPending to deactivate gradient when pending requests are resolved
  useEffect(() => {
    if (hasPending <= 0) {
      setGradientActive(false);
    }
  }, [hasPending]);

  console.log(`activeSwarmCard: Mounted with swarm ID ${swarm.id} swarm: ${JSON.stringify(swarm)}`);

  useEffect(() => {
    setLocalSwarm(swarm);
  }, [swarm]);

  // SignalR listener: Listen for the "CreateSwarmBuyRequest" event
  useEffect(() => {
    // Event handler for CreateSwarmBuyRequest event
    const handleNewBuyRequest = (req) => {
        // Check if the event corresponds to this swarm and the card is not selected
        if (req.swarmId === swarm.id && !isSelected) {
          console.log(`ActiveSwarmCard: Received CreateSwarmBuyRequest for swarm ID ${swarm.id}`);
          setHighlight(true);
          setGradientActive(true);
          // Clear any existing timeout to avoid overlapping
          if (highlightTimeoutRef.current) {
            clearTimeout(highlightTimeoutRef.current);
          }
          // Remove highlight after 1 second
          highlightTimeoutRef.current = setTimeout(() => {
            console.log(`ActiveSwarmCard: Removing highlight for swarm ID ${swarm.id}`);
            setHighlight(false);
            highlightTimeoutRef.current = null;
          }, 1000);
        }
      };
  
      // Attach the event handler if the connection exists
      if (signalREncorehive) {
        signalREncorehive.on('CreateSwarmBuyRequest', handleNewBuyRequest);
      }
  
      // Cleanup: remove the event handler and clear the timeout on unmount
      return () => {
        if (signalREncorehive) {
          signalREncorehive.off('CreateSwarmBuyRequest', handleNewBuyRequest);
        }
        if (highlightTimeoutRef.current) {
          clearTimeout(highlightTimeoutRef.current);
        }
      };
  }, [signalREncorehive, swarm.id, isSelected]);

  /**
   * Handle card click to select or deselect the swarm.
   * Also deactivate the gradient when the card is selected.
   */
  const handleCardClick = () => {
    console.log(`activeSwarmCard: Card clicked for swarm ID ${swarm.id}, isSelected = ${isSelected}`);
    if (!isSelected) {
      dispatch(setSwarmIdSelected(swarm.id)); // Dispatch Redux action
      console.log(`activeSwarmCard: Dispatched setSwarmIdSelected for swarm ID ${swarm.id}`);
      setGradientActive(false); // Deactivate gradient when selecting the card
      console.log(`activeSwarmCard: Gradient deactivated for swarm ID ${swarm.id}`);
    } else {
      console.log(`activeSwarmCard: Swarm ID ${swarm.id} is already selected`);
    }
  };

  /**
   * Handle copying the swarm code to the clipboard.
   * 
   * @param {string} code - The swarm code to copy.
   */
  const handleCopy = useCallback(async (code) => {
    console.log(`activeSwarmCard: Attempting to copy code for swarm ID ${swarm.id}`);
    try {
      await copyToClipBoard(code);
      console.log(`activeSwarmCard: Code copied successfully for swarm ID ${swarm.id}: ${code}`);
      // Optional: Display a success notification
    } catch (error) {
      console.error(`activeSwarmCard: Error copying the code for swarm ID ${swarm.id}:`, error);
      // Optional: Display an error notification to the user
    }
  }, [swarm.id]);

  const handleEdit = useCallback(() => {
    console.log(`activeSwarmCard: Edit initiated for swarm ID ${swarm.id}`);
    setIsEditModalOpen(true);
  }, [swarm.id]);

  const handleCloseEditModal = useCallback(() => {
    setIsEditModalOpen(false);
  }, []);

  // Determines card shadow based on pending items
  const getCardShadow = (hasPending) => {
    return hasPending ? CARD_SHADOWS.PENDING : CARD_SHADOWS.DEFAULT;
  };

  async function handleStartSwarmClick(e) {
    console.log('PARENT, Start button clicked for swarm ID', swarm.id);
    let body={
      id: swarm.id,
      running: true
    };
    //error handling managed by useEffect that listen 'editSwarmError'
    try {
      const editedSwarm = await editSwarm(body).unwrap();
      setLocalSwarm(editedSwarm);
      console.log(`activeSwarmCard: Swarm ID ${swarm.id} started successfully`);
    } catch (error) {
      if(error.status === 401) return; //Unauthorized handled

      console.error(`activeSwarmCard: Error starting swarm ID ${swarm.id}:`, error);
      setError(createErrorMessage(error));
    }
  }

  async function handleStopSwarmClick(e) {
    console.log('PARENT, Stop button clicked for swarm ID', swarm.id);
    let body={
      id: swarm.id,
      running: false
    };
    //error handling managed by useEffect that listen 'editSwarmError'
    try {
      const editedSwarm = await editSwarm(body).unwrap();
      setLocalSwarm(editedSwarm);
      console.log(`activeSwarmCard: Swarm ID ${swarm.id} stopped successfully`);
    } catch (error) {
      if(error.status === 401) return; //Unauthorized handled

      console.error(`activeSwarmCard: Error stopping swarm ID ${swarm.id}:`, error);
      setError(createErrorMessage(error));
    }
  }

    // Handler for when the "X" is clicked to hide the card permanently
    const handleCloseCard = () => {
      console.log(`activeSwarmCard: Closing card for swarm ID ${swarm.id}`);
      
      // Update the swarm in the backend marking it as closed
      editSwarm({
        id: swarm.id,
        isClosed: true
      })
        .unwrap()
        .then(() => {
          console.log(`Swarm ${swarm.id} marked as closed successfully`);
        })
        .catch((error) => {
          console.error(`Error closing swarm ${swarm.id}:`, error);
          showNotification(createErrorMessage(error), 'error');
        });
    };

  return (
    <>
      {/* Container for the card and the conditional close "X" */}
      <div
        style={{
          position: 'relative',
          display: 'inline-block'
        }}
      >
        {/* Conditionally render the close "X" at the top-right if localSwarm.running is false*/}
        {(!localSwarm.running && buyManager?.buyManagerID === swarm.buyManagerId ) && (
          <div
            style={{
              position: 'absolute',
              top: '-10px',
              right: '-10px',
              backgroundColor: !hasPending ? 'red' : 'grey',
              color: 'white',
              borderRadius: '50%',
              width: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              zIndex: 3
            }}
            //A: if the the swarms must be stopped and has no pending buy requests in order to close it
            onClick={ ()=> !hasPending && handleCloseCard()}
          >
            X
          </div>
        )}
  
        {/* Card content */}
        <div
          className={`swarm-card ${highlight && !isSelected ? 'highlight' : ''}`}
          style={{
            width: '230px',
            borderRadius: '0.75em',
            backgroundColor: '#FFFFFF',
            padding: '0.5em',
            boxShadow: getCardShadow(hasPending > 0),
            border: isSelected ? `3px solid ${theme.palette.primary.main}` : 'none',
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden'
          }}
          onClick={handleCardClick}
        >
          {/* Card Content with higher z-index to appear above the gradient overlay */}
          <div
            style={{
              position: 'relative',
              zIndex: 2,
              display: 'flex',
              flexDirection: 'row',
              gap: '8px',
              width: '100%'
            }}
          >
            <div
              style={{
                flex: 1,
                minWidth: 0 // Important for flex-shrink to work correctly
              }}
            >
              {/* Event Information */}
              <EventInfo
                event={swarm.event}
                primaryEventUrl={swarm.alternativeEventUrl || swarm.event?.entityLink?.href}
                eventDate={swarm.event.date}
                code={swarm.code}
                onCopy={handleCopy}
              />
  
              {/* Additional Information */}
              <AdditionalInfo
                completed={swarm.completed}
                accepted={swarm.accepted}
                isNow={swarm.isNow}
                buyManagerId={swarm.buyManagerId}
                isAutoBuyActive={swarm.isAutoBuyActive}
              />
            </div>
  
            {/* Side Icons */}
            <div style={{ flexShrink: 0, width: '34px' }}>
              <SwarmCardHeader
                isNow={swarm.isNow}
                running={localSwarm.running}
                primaryColor={theme.palette.primary.main}
                bteId={swarm.bteId}
                onEdit={handleEdit}
                handleStartSwarmClick={handleStartSwarmClick}
                handleStopSwarmClick={handleStopSwarmClick}
                editSwarmIsLoading={editSwarmIsLoading}
              />
            </div>
          </div>
  
          {/* Conditionally render the gradient overlay */}
          {gradientActive && (
            <GradientOverlay
              active={gradientActive}
              background={`linear-gradient(to bottom, ${theme.palette.primary.main}80, ${theme.palette.primary.main}00)`}
            />
          )}
        </div>
      </div>
      
      {
        isEditModalOpen && (
          <SwarmEditModal
            isOpen={isEditModalOpen}
            onClose={handleCloseEditModal}
            bteId={swarm.bteId}
            swarmProp={swarm}
          />
        )
      }
      
    </>
  );
  
};

ActiveSwarmCard.propTypes = {
  swarm: PropTypes.shape({
    id: PropTypes.number.isRequired,
    bteId: PropTypes.number.isRequired,
    running: PropTypes.bool,
    salesDate: PropTypes.string.isRequired,
    isNow: PropTypes.bool.isRequired,
    bought: PropTypes.number,
    pending: PropTypes.number,
    accepted: PropTypes.number,
    code: PropTypes.string.isRequired,
    event: PropTypes.shape({
      bteId: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      date: PropTypes.string.isRequired,
      performers: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
      })),
      venue: PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        streetAddress1: PropTypes.string.isRequired,
        streetAddress2: PropTypes.string,
        city: PropTypes.string,
        state: PropTypes.string,
      }),
    }),
    timeSlotLabel: PropTypes.string.isRequired,
  }).isRequired,
};

ActiveSwarmCard.defaultProps = {
  swarm: {
    pending: 0,
    bought: 0,
    accepted: 0,
  },
};

export default ActiveSwarmCard;
